import { DEFAULT_POINT_STATUSES } from 'application/pages/PointsPage/renderer/PointsToolbar/consts';
import useAllPointList from 'domain/point/useAllPointList';
import { useCallback, useEffect, useMemo } from 'react';
import { IPointGroupDevice } from 'application/pages/PointsGroupsPage/types';
import { useAppDispatch, useAppSelector } from 'application/providers/StoreProvider';
import { selectCompanyIds } from 'application/store';
import {
  PointsListCheckboxGroupPointType,
  PointsListDataItem,
} from '../../PointsGroupDrawer/PointsGroupsPointsList/types';
import { selectDevices } from '../../slices/createPointsGroup/selectors';
import { setDevices, setStep } from '../../slices/createPointsGroup/slice';
import { CREATE_POINTS_GROUP_STEPS } from '../../slices/createPointsGroup/consts';

export const useSelectPointsGroupPointsViewModel = () => {
  const companyIds = useAppSelector(selectCompanyIds);
  const dispatch = useAppDispatch();
  const allPointList = useAllPointList({
    companyIds,
    playingStatuses: DEFAULT_POINT_STATUSES,
    hasVideoSupport: true,
    withDevices: true,
  });

  const devices = useAppSelector(selectDevices);

  const updatePointsGroup = (points: PointsListDataItem[]) => {
    const devicesMap = allPointList.points?.reduce(
      (acc, it) => {
        if (it?.device?.id) {
          acc[it.id] = { ...it.device, point: it };
        }

        return acc;
      },
      {} as { [key: string]: PointsListCheckboxGroupPointType['device'] & { point: PointsListDataItem } }
    );

    const devices = points?.map((it) => devicesMap?.[it.id]).filter(Boolean) as IPointGroupDevice[];

    if (devices) {
      dispatch(setDevices(devices));
    }
  };

  const onNext = () => {
    dispatch(setStep(CREATE_POINTS_GROUP_STEPS.MASTER_VIDEO_STREAM));
  };

  const onBack = () => {
    dispatch(setStep(CREATE_POINTS_GROUP_STEPS.TITLE));
  };

  const onPointsChange = (pointsIds: PointsListDataItem[]) => {
    if (!allPointList.points) return;

    const pointsSet = new Set(pointsIds.map(({ id }) => String(id)));

    const newSelectedPoints = allPointList.points
      ?.filter((point) => pointsSet.has(String(point.id)))
      .map((it) => ({
        ...it,
        disabled: Boolean(it.device?.group?.id),
      }));

    updatePointsGroup(newSelectedPoints);
  };

  useEffect(() => {
    if (companyIds.length > 0) {
      allPointList.request();
    }
  }, [companyIds]);

  const loadMore = useCallback(async () => {
    if (allPointList.loading || !allPointList.paginationInfo?.hasNextPage) {
      return;
    }

    allPointList.fetchMore(allPointList.paginationInfo?.page);
  }, [allPointList]);

  const selectedPoints = useMemo(() => {
    const devicesMap = allPointList.points?.reduce(
      (acc, it) => {
        if (it?.device?.id) {
          acc[it.id] = { ...it.device, point: it };
        }

        return acc;
      },
      {} as { [key: string]: PointsListCheckboxGroupPointType['device'] & { point: PointsListDataItem } }
    );

    return devices && allPointList.points
      ? (devices
          ?.map((it) => devicesMap && it.point && devicesMap?.[it?.point?.id]?.point)
          .filter(Boolean) as PointsListDataItem[])
      : [];
  }, [devices, allPointList.points]);

  return {
    allPointList,
    loadMore,
    onPointsChange,
    updatePointsGroup,
    selectedPoints,
    isButtonLoading: allPointList.loading,
    isButtonDisabled: selectedPoints?.length === 0,
    onNext,
    onBack,
  };
};
